/**
 * Model Group Tool - Group Creation
 *
 * Groups the selected elements into a Model Group. Both selection stores
 * (app + BIM) are merged, matching how the Ribbon resolves selections.
 */

import { v4 as uuidv4 } from 'uuid';
import { GroupElement } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import i18n from '../i18n';

export class ModelGroupTool extends BaseTool {
    id = 'model_group';
    name = 'Model Group';
    cursor = 'default';

    activate(context: Parameters<BaseTool['activate']>[0]): void {
        super.activate(context);
        this.createGroup();
        // Immediately return to select after the group is made.
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void { }
    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createGroup() {
        if (!this.context) return;

        // Merge both selection registries so the group picks up whatever the
        // viewport or BIM store considers selected.
        const ids = new Set<string>([
            ...this.context.appStore.selectedIds,
            ...this.context.bimStore.selection,
        ]);
        // A group cannot contain a group — keep only real model elements.
        const memberIds = Array.from(ids).filter((id) => {
            const el = this.context!.bimStore.project.elements.get(id);
            return !!el && el.category !== 'Model Groups';
        });

        if (memberIds.length === 0) {
            this.notify(i18n.t('tools.notify.selectOneOrMoreElementsBefore'), 'warning');
            return;
        }

        // Origin: centroid of member start/position points (fallback 0,0,0).
        let sx = 0, sy = 0, sz = 0, n = 0;
        memberIds.forEach((id) => {
            const g = (this.context!.bimStore.project.elements.get(id) as any)?.geometryData;
            const p = g?.position ?? g?.start;
            if (Array.isArray(p)) {
                sx += p[0]; sy += p[1]; sz += p[2] ?? 0; n++;
            }
        });
        const origin: [number, number, number] = n > 0 ? [sx / n, sy / n, sz / n] : [0, 0, 0];

        let maxIndex = 0;
        this.context.bimStore.project.elements.forEach((el) => {
            const m = /^Group (\d+)$/.exec(el.name ?? '');
            if (m) maxIndex = Math.max(maxIndex, parseInt(m[1], 10));
        });
        const name = `Group ${maxIndex + 1}`;

        const group: GroupElement = {
            id: uuidv4(),
            category: 'Model Groups',
            type: 'Model Group',
            name,
            parameters: {
                'Name': name,
                'Members': memberIds.length,
                'Origin': `${origin[0].toFixed(1)}, ${origin[1].toFixed(1)}`,
            },
            constraints: [],
            geometryData: {
                origin,
                memberIds,
            },
        };

        this.context.bimStore.addElement(group);
        this.notify(i18n.t('tools.notify.groupCreatedWith', { name: name, count: memberIds.length }), 'success');
    }
}
